/**
 * Utility functions for client-side navigation that preserve attribution
 * query parameters (UTM + Google Ads click identifiers).
 *
 * Behavior:
 * - Keeps only whitelisted attribution parameters from the current URL.
 * - Drops non-attribution params (for example: "step", "insuranceType") so
 *   form-internal state does not leak into other routes.
 * - Merges any caller-provided `additionalParams` on top.
 * - Uses the shared attribution key registry (`ATTRIBUTION_PARAM_KEYS`) from
 *   `attributionUtils.ts` to keep navigation behavior aligned with attribution
 *   capture and reset-preservation logic.
 */

import { ATTRIBUTION_PARAM_KEYS } from "@/utils/attributionUtils";
import { captureCroTrackingFromPath } from "@/utils/croTracking";
import { trackEvent } from "@/utils/formUtils";
import { ensureFirstLandingPage } from "@/utils/firstLandingPage";
import { syncReferralEntryForNavigation } from "@/utils/referralFlow";
import { scheduleIdleTask } from "@/utils/scheduleIdleTask";

/**
 * Query parameters that should persist across page navigation.
 * Sourced from a shared attribution key registry to avoid drift between
 * navigation, form capture, and reset-preservation logic.
 */
const PERSISTENT_PARAMS = [
  ...ATTRIBUTION_PARAM_KEYS,
] as const;

const normalizePath = (path: string): string => {
  if (!path) return "/";
  const noQuery = path.split("?")[0] || "/";
  return noQuery.endsWith("/") && noQuery.length > 1
    ? noQuery.slice(0, -1)
    : noQuery;
};

const getFormIdFromPath = (
  path: string,
): "compare" | "compare-2" | "auto-compare" | null => {
  if (path === "/auto-compare") return "auto-compare";
  if (path.startsWith("/compare-2")) return "compare-2";
  if (path.startsWith("/compare")) return "compare";
  return null;
};

/**
 * Builds a URL path with preserved query parameters from the current URL
 * @param targetPath - The path to navigate to (e.g., "/compare")
 * @param additionalParams - Optional additional query parameters to append
 * @returns The complete URL with preserved query parameters
 */
export function buildUrlWithQueryParams(
  targetPath: string,
  additionalParams?: Record<string, string>
): string {
  // Get current query parameters from the window location
  const currentParams = new URLSearchParams(window.location.search);
  const filteredParams = new URLSearchParams();

  // Only keep whitelisted params from current URL
  PERSISTENT_PARAMS.forEach((param) => {
    const value = currentParams.get(param);
    if (value) {
      filteredParams.set(param, value);
    }
  });

  // Add any additional parameters (these can override or add to the filtered params)
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      filteredParams.set(key, value);
    });
  }

  // Build the final URL
  const queryString = filteredParams.toString();
  return queryString ? `${targetPath}?${queryString}` : targetPath;
}

/**
 * Navigates to a path while preserving attribution query parameters.
 *
 * This function intentionally uses `router.push` (not `replace`) so browser
 * back/forward navigation still reflects user route transitions.
 *
 * @param router - Next.js router instance
 * @param targetPath - The path to navigate to (e.g., "/compare")
 * @param additionalParams - Optional additional query parameters to append
 */
export function navigateWithQueryParams(
  router: { push: (url: string) => void; replace?: (url: string) => void },
  targetPath: string,
  additionalParams?: Record<string, string>
): void {
  const url = buildUrlWithQueryParams(targetPath, additionalParams);

  if (typeof window !== "undefined") {
    const sourcePath = normalizePath(window.location.pathname || "/");
    captureCroTrackingFromPath(sourcePath);
    const targetBasePath = normalizePath(targetPath);
    syncReferralEntryForNavigation(sourcePath, targetBasePath);
    const targetFormId = getFormIdFromPath(targetBasePath);
    const sourceFormId = getFormIdFromPath(sourcePath);
    const isAlreadyInsideCompareFlow = sourceFormId !== null;
    const shouldTrackEntryClick =
      targetFormId !== null && !isAlreadyInsideCompareFlow;

    // Navigate first so the browser can paint before analytics work (INP).
    router.push(url);

    if (shouldTrackEntryClick) {
      scheduleIdleTask(() => {
        trackEvent("insurance.form_entry_click", {
          form_id: targetFormId,
          source_path: sourcePath,
          destination_path: targetBasePath,
          first_landing_page: ensureFirstLandingPage(sourcePath),
          event_id: `entry_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
        });
      });
    }

    return;
  }

  router.push(url);
}

export function compareFormEntryParams(insuranceType: string): Record<string, string> {
  return {
    step: "insurance-type",
    insuranceType,
  };
}
